import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BAY_H_PREFIX,
  BAY_U_PREFIX,
  GROUP_HARVESTED,
  GROUP_UNHARVESTED,
  STATUS,
  UNSAVED_FILTER_LABEL,
} from "./harvestEntryConstants.js";

export function useHarvestEntryFilters({
  rows,
  activeGreenhouseIds,
  statusFilter,
  searchText,
  bayRowFilter,
  setBayRowFilter,
  statusColFilterValue,
  sortColumns,
  getBackendStatus,
  getBackendStatusText,
  getEffectiveStatusText,
  rowHasPendingChanges,
  pairKeyOfRow,
  naturalCompare,
}) {
  const statusOptions = useMemo(
    () => [UNSAVED_FILTER_LABEL, "Planted", "Harvested", "Discarded"],
    [],
  );

  const baseRowsForOptions = useMemo(() => {
    let out = [...rows];

    if (activeGreenhouseIds.length) {
      out = out.filter((r) => activeGreenhouseIds.includes(r.greenhouse_id));
    }

    if (statusFilter === "harvested") {
      out = out.filter((r) => getBackendStatus(r) === STATUS.HARVESTED);
    } else if (statusFilter === "planted") {
      out = out.filter((r) => getBackendStatus(r) === STATUS.PLANTED);
    } else if (statusFilter === "discarded") {
      out = out.filter((r) => getBackendStatus(r) === STATUS.DISCARDED);
    }

    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      out = out.filter(
        (r) =>
          r.greenhouse_name?.toLowerCase().includes(q) ||
          r.bay_name?.toLowerCase().includes(q) ||
          r.row_name?.toLowerCase().includes(q),
      );
    }

    if (statusColFilterValue === UNSAVED_FILTER_LABEL) {
      out = out.filter(rowHasPendingChanges);
    } else if (statusColFilterValue) {
      out = out.filter((r) => getBackendStatusText(r) === statusColFilterValue);
    }

    return out;
  }, [
    rows,
    activeGreenhouseIds,
    statusFilter,
    searchText,
    statusColFilterValue,
    getBackendStatus,
    getBackendStatusText,
    rowHasPendingChanges,
  ]);

  const {
    bayUToPairKeys,
    bayHToPairKeys,
    bayRowTreeData,
    bayRowGroupExpandedKeys,
    bayRowNodeKeys,
  } = useMemo(() => {
    const pairIsHarvested = new Map(); // pairKey -> boolean
    for (const r of baseRowsForOptions) {
      const pk = pairKeyOfRow(r);
      const backend = getBackendStatus(r);
      const harvestedLike =
        backend === STATUS.HARVESTED || backend === STATUS.DISCARDED;

      pairIsHarvested.set(
        pk,
        (pairIsHarvested.get(pk) ?? false) || harvestedLike,
      );
    }

    const uMap = new Map(); // bay -> Set(pairKey)
    const hMap = new Map(); // bay -> Set(pairKey)

    for (const r of baseRowsForOptions) {
      const bay = r.bay_name;
      const pk = pairKeyOfRow(r);
      const isH = pairIsHarvested.get(pk) === true;

      const target = isH ? hMap : uMap;
      if (!target.has(bay)) target.set(bay, new Set());
      target.get(bay).add(pk);
    }

    const buildGroupChildren = (map, bayPrefix) => {
      const bays = Array.from(map.keys()).sort(naturalCompare);

      return bays.map((bay) => {
        const pairs = Array.from(map.get(bay) || []);
        const rowsList = pairs
          .map((pk) => pk.split("__")[1])
          .filter(Boolean)
          .sort(naturalCompare);

        return {
          title: String(bay),
          value: `${bayPrefix}${bay}`,
          key: `${bayPrefix}${bay}`,
          children: rowsList.map((rowName) => {
            const pk = `${bay}__${rowName}`;
            return { title: String(rowName), value: pk, key: pk };
          }),
        };
      });
    };

    const treeData = [];
    const expandedKeys = [];
    const nodeKeys = new Set();

    const unharvestedChildren = buildGroupChildren(uMap, BAY_U_PREFIX);
    for (const bay of uMap.keys()) {
      nodeKeys.add(`${BAY_U_PREFIX}${bay}`);
    }
    if (unharvestedChildren.length) {
      treeData.push({
        title: "Unharvested",
        value: GROUP_UNHARVESTED,
        key: GROUP_UNHARVESTED,
        children: unharvestedChildren,
      });
      expandedKeys.push(GROUP_UNHARVESTED);
    }

    const harvestedChildren = buildGroupChildren(hMap, BAY_H_PREFIX);
    for (const bay of hMap.keys()) {
      nodeKeys.add(`${BAY_H_PREFIX}${bay}`);
    }
    if (harvestedChildren.length) {
      treeData.push({
        title: "Harvested",
        value: GROUP_HARVESTED,
        key: GROUP_HARVESTED,
        children: harvestedChildren,
      });
      expandedKeys.push(GROUP_HARVESTED);
    }

    return {
      bayUToPairKeys: uMap,
      bayHToPairKeys: hMap,
      bayRowTreeData: treeData,
      bayRowGroupExpandedKeys: expandedKeys,
      bayRowNodeKeys: nodeKeys,
    };
  }, [baseRowsForOptions, getBackendStatus, naturalCompare, pairKeyOfRow]);

  const [bayRowExpandedKeys, setBayRowExpandedKeys] = useState(
    bayRowGroupExpandedKeys,
  );

  useEffect(() => {
    const valid = new Set([...bayRowGroupExpandedKeys, ...bayRowNodeKeys]);
    setBayRowExpandedKeys((prev) => {
      const next = new Set(prev);
      for (const key of bayRowGroupExpandedKeys) next.add(key);
      return Array.from(next).filter((k) => valid.has(k));
    });
  }, [bayRowGroupExpandedKeys, bayRowNodeKeys]);

  const handleBayRowFilterChange = useCallback(
    (values, _label, extra) => {
      const nextValues = values || [];
      setBayRowFilter(nextValues);

      const triggerValue = extra?.triggerValue
        ? String(extra.triggerValue)
        : "";

      if (
        !triggerValue ||
        !extra?.checked ||
        (!triggerValue.startsWith(BAY_U_PREFIX) &&
          !triggerValue.startsWith(BAY_H_PREFIX))
      ) {
        return;
      }

      setBayRowExpandedKeys((prev) => {
        const next = new Set(prev);
        next.add(triggerValue);
        const valid = new Set([...bayRowGroupExpandedKeys, ...bayRowNodeKeys]);
        return Array.from(next).filter((k) => valid.has(k));
      });
    },
    [bayRowGroupExpandedKeys, bayRowNodeKeys, setBayRowFilter],
  );

  const addAllPairsFromMap = (map, intoSet) => {
    for (const set of map.values()) {
      for (const pk of set) intoSet.add(pk);
    }
  };

  const expandBayRowSelectionToPairs = useCallback(
    (values) => {
      const selectedPairs = new Set();

      for (const v of values || []) {
        const s = String(v || "");

        if (s === GROUP_UNHARVESTED) {
          addAllPairsFromMap(bayUToPairKeys, selectedPairs);
          continue;
        }
        if (s === GROUP_HARVESTED) {
          addAllPairsFromMap(bayHToPairKeys, selectedPairs);
          continue;
        }

        if (s.startsWith(BAY_U_PREFIX)) {
          const bay = s.slice(BAY_U_PREFIX.length);
          const set = bayUToPairKeys.get(bay);
          if (set) for (const pk of set) selectedPairs.add(pk);
          continue;
        }
        if (s.startsWith(BAY_H_PREFIX)) {
          const bay = s.slice(BAY_H_PREFIX.length);
          const set = bayHToPairKeys.get(bay);
          if (set) for (const pk of set) selectedPairs.add(pk);
          continue;
        }

        selectedPairs.add(s);
      }

      return selectedPairs;
    },
    [bayHToPairKeys, bayUToPairKeys],
  );

  const filteredRows = useMemo(() => {
    let out = [...rows];

    if (activeGreenhouseIds.length) {
      out = out.filter((r) => activeGreenhouseIds.includes(r.greenhouse_id));
    }

    if (statusFilter === "harvested") {
      out = out.filter((r) => getBackendStatus(r) === STATUS.HARVESTED);
    } else if (statusFilter === "planted") {
      out = out.filter((r) => getBackendStatus(r) === STATUS.PLANTED);
    } else if (statusFilter === "discarded") {
      out = out.filter((r) => getBackendStatus(r) === STATUS.DISCARDED);
    }

    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      out = out.filter(
        (r) =>
          r.greenhouse_name?.toLowerCase().includes(q) ||
          r.bay_name?.toLowerCase().includes(q) ||
          r.row_name?.toLowerCase().includes(q),
      );
    }

    if (bayRowFilter.length) {
      const selectedPairs = expandBayRowSelectionToPairs(bayRowFilter);
      out = out.filter((r) => selectedPairs.has(pairKeyOfRow(r)));
    }

    if (statusColFilterValue === UNSAVED_FILTER_LABEL) {
      out = out.filter(rowHasPendingChanges);
    } else if (statusColFilterValue) {
      out = out.filter((r) => getBackendStatusText(r) === statusColFilterValue);
    }

    return out;
  }, [
    rows,
    activeGreenhouseIds,
    statusFilter,
    searchText,
    bayRowFilter,
    statusColFilterValue,
    getBackendStatus,
    getBackendStatusText,
    expandBayRowSelectionToPairs,
    rowHasPendingChanges,
    pairKeyOfRow,
  ]);

  const sortedRows = useMemo(() => {
    if (!sortColumns?.length) {
      const out = [...filteredRows];
      out.sort((a, b) => {
        const ghCmp = naturalCompare(a.greenhouse_name, b.greenhouse_name);
        if (ghCmp !== 0) return ghCmp;
        return naturalCompare(a.row_name, b.row_name);
      });
      return out;
    }

    const compare = (a, b, key) => {
      if (key === "bay_row") {
        const bayCmp = naturalCompare(a.bay_name, b.bay_name);
        if (bayCmp !== 0) return bayCmp;
        return naturalCompare(a.row_name, b.row_name);
      }
      if (key === "status") {
        return String(getEffectiveStatusText(a)).localeCompare(
          String(getEffectiveStatusText(b)),
        );
      }
      return 0;
    };

    const out = [...filteredRows];
    out.sort((a, b) => {
      for (const s of sortColumns) {
        const dir = s.direction === "DESC" ? -1 : 1;
        const c = compare(a, b, s.columnKey);
        if (c !== 0) return c * dir;
      }
      return 0;
    });
    return out;
  }, [filteredRows, sortColumns, getEffectiveStatusText, naturalCompare]);

  const visibleRows = useMemo(() => sortedRows, [sortedRows]);

  return {
    statusOptions,
    bayRowTreeData,
    bayRowExpandedKeys,
    setBayRowExpandedKeys,
    handleBayRowFilterChange,
    visibleRows,
  };
}
